import {
  Button,
  column,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  multiFilter,
  PageHeaderLayout,
  RowStyles,
  simpleHeader,
  SimpleHeaderAndData,
  singleFilter,
  useGridTableLayoutState,
  useSuperDrawer,
} from "@homebound/beam";
import { useMemo } from "react";
import { emptyCellDash } from "src/components";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, ItemFilter } from "src/generated/graphqlTypes";
import {
  AdminItemFragment,
  ItemsPageMetadataQuery,
  ItemsQuery,
  useItemsPageMetadataQuery,
  useItemsQuery,
} from "src/routes/admin/items/ItemsPage.generated";
import { queryResult } from "src/utils";
import { AdminItemsDrawer } from "./AdminItemsDrawer";

/** Lists all variables available in the system, allows adding new ones. */
export function ItemsPage() {
  const query = useItemsPageMetadataQuery();
  const isReadonly = !useFeatureFlag(FeatureFlagType.ItemCodesAdmin);

  return queryResult(query, {
    data: ({ costCodes }) => {
      return <ItemsPageDataView costCodes={costCodes} isReadonly={isReadonly} />;
    },
  });
}

function ItemsPageDataView(props: { costCodes: ItemsPageMetadataQuery["costCodes"]; isReadonly: boolean }) {
  const { costCodes, isReadonly } = props;
  const { openInDrawer } = useSuperDrawer();
  const isMaterialCatalogEnabled = useFeatureFlag(FeatureFlagType.MaterialCatalog);
  const isOptionCatalogV2Enabled = useFeatureFlag(FeatureFlagType.OptionsCatalogV2);

  const filterDefs = useMemo(() => {
    return {
      costCode: multiFilter({
        options: costCodes,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => `${o.displayName} V${o.version}`,
      }),
      version: singleFilter({
        options: [
          { id: 1, name: "1" },
          { id: 2, name: "2" },
        ],
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
    };
  }, [costCodes]);

  const layoutState = useGridTableLayoutState<LocalItemFilter>({
    persistedFilter: { storageKey: "itemsFilter", filterDefs },
    search: "client",
  });

  const query = useItemsQuery({
    variables: {
      // Map our version number -> backend number[]
      filter: { ...layoutState.filter, version: layoutState.filter.version ? [layoutState.filter.version] : [1, 2] },
    },
  });

  const rowStyles = useMemo(
    () =>
      ({
        header: {},
        data: {
          onClick: (d) =>
            openInDrawer({
              content: (
                <AdminItemsDrawer
                  selectedItem={d.data}
                  isMaterialCatalogEnabled={isMaterialCatalogEnabled}
                  costCodes={costCodes || []}
                  unitsOfMeasure={query.data?.unitsOfMeasure || []}
                  isOptionCatalogV2Enabled={isOptionCatalogV2Enabled}
                  isReadonly={isReadonly}
                />
              ),
            }),
        },
      }) as RowStyles<Row>,
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data],
  );

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Items",
        rightSlot: isReadonly ? undefined : (
          <Button
            variant="secondary"
            label="Add Item"
            onClick={() =>
              openInDrawer({
                content: (
                  <AdminItemsDrawer
                    selectedItem={undefined}
                    isMaterialCatalogEnabled={isMaterialCatalogEnabled}
                    costCodes={costCodes || []}
                    unitsOfMeasure={query.data?.unitsOfMeasure || []}
                    isOptionCatalogV2Enabled={isOptionCatalogV2Enabled}
                    isReadonly={isReadonly}
                  />
                ),
              })
            }
          />
        ),
      }}
    >
      <GridTableLayout
        hideEditColumns
        layoutState={layoutState}
        tableProps={{ sorting: { on: "client" }, query, createRows, rowStyles, columns, filterMaxRows: 100 }}
      />
    </PageHeaderLayout>
  );
}

type Row = SimpleHeaderAndData<AdminItemFragment>;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: "ID", data: ({ id }) => id, w: "60px" }),
  column<Row>({
    header: "Item Code",
    data: (d) => `${d.costCode.number}.${String(d.itemCode).padStart(3, "0")}`,
    w: "120px",
  }),
  column<Row>({ header: "Name", data: (d) => d.name, mw: "200px" }),
  column<Row>({ header: "Cost Code", data: (d) => `${d.costCode.number} ${d.costCode.name}`, w: "220px" }),
  column<Row>({ header: "Material Code", data: (d) => d.materialCodePrefix, w: "140px" }),
  column<Row>({
    header: "Dimensions",
    data: (d) => ({
      content: d.materialAttributeDimensions.isEmpty ? emptyCellDash : d.materialAttributeDimensions.length,
      value: d.materialAttributeDimensions.length ?? 0,
    }),
    w: "120px",
  }),
];

function createRows(data: ItemsQuery | undefined): GridDataRow<Row>[] {
  return [simpleHeader, ...(data?.items.map((v) => ({ kind: "data" as const, id: v.id, data: v })) || [])];
}

// Replace version with just `number` so we can use singleFilter
type LocalItemFilter = Omit<ItemFilter, "version"> & { version: number };
